import React, { useEffect, useState } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, downloadExport } from '../lib/api';
import { Package, Plus, Edit2, Trash2, Search, X, Download, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

export default function ProductsPage() {
  const [products, setProducts] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState(null);
  const [form, setForm] = useState({ product_name: '', sku: '', category: '', hsn_code: '', unit: 'PCS', description: '' });
  const [exportingProducts, setExportingProducts] = useState(false);

  const load = () => {
    setLoading(true);
    getProducts({ search }).then(r => { setProducts(r.data.products || []); setTotal(r.data.total || 0); setLoading(false); }).catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, [search]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editItem) { await updateProduct(editItem._id, form); } else { await createProduct(form); }
      setShowForm(false); setEditItem(null); setForm({ product_name: '', sku: '', category: '', hsn_code: '', unit: 'PCS', description: '' }); load();
    } catch (e) { alert(e.response?.data?.detail || 'Error'); }
  };

  const startEdit = (p) => {
    setEditItem(p);
    setForm({ product_name: p.product_name, sku: p.sku || '', category: p.category || '', hsn_code: p.hsn_code || '', unit: p.unit || 'PCS', description: p.description || '' });
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    try { await deleteProduct(id); load(); } catch (e) { alert('Error'); }
  };

  const handleExportProducts = async () => {
    setExportingProducts(true);
    try {
      const success = await downloadExport('/api/boss/scrm/products/export/csv', `products_export_${new Date().toISOString().slice(0,10)}.csv`);
      if (success) toast.success('Products exported as CSV');
      else toast.error('Export failed');
    } catch { toast.error('Export failed'); }
    setExportingProducts(false);
  };

  return (
    <div className="space-y-4" data-testid="products-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-boss-text">Product Catalogue</h1>
          <p className="text-boss-text-muted text-sm">{total} products</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={handleExportProducts}
            disabled={exportingProducts}
            className="flex items-center gap-2 px-3 py-2 rounded-lg border border-boss-border text-sm hover:bg-boss-surface-2 disabled:opacity-50 transition-colors"
            data-testid="export-products-btn"
          >
            {exportingProducts ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Export CSV
          </button>
          <button
            onClick={() => { setShowForm(true); setEditItem(null); setForm({ product_name: '', sku: '', category: '', hsn_code: '', unit: 'PCS', description: '' }); }}
            className="flex items-center gap-2 px-4 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2"
            data-testid="add-product-btn"
          >
            <Plus size={16} /> Add Product
          </button>
        </div>
      </div>

      <div className="relative max-w-md">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-boss-text-muted" />
        <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)} className="w-full pl-9 pr-4 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="product-search" />
      </div>

      {showForm && (
        <div className="bg-white rounded-xl p-6 shadow-sm border border-boss-border">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-semibold text-boss-text">{editItem ? 'Edit Product' : 'Add Product'}</h3>
            <button onClick={() => { setShowForm(false); setEditItem(null); }}><X size={18} /></button>
          </div>
          <form onSubmit={handleSubmit} className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">Product Name *</label>
              <input required value={form.product_name} onChange={e => setForm(f => ({...f, product_name: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" data-testid="product-name-input" />
            </div>
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">SKU</label>
              <input value={form.sku} onChange={e => setForm(f => ({...f, sku: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">Category</label>
              <input value={form.category} onChange={e => setForm(f => ({...f, category: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">HSN Code</label>
              <input value={form.hsn_code} onChange={e => setForm(f => ({...f, hsn_code: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            </div>
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">Unit</label>
              <select value={form.unit} onChange={e => setForm(f => ({...f, unit: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none">
                <option>PCS</option><option>MTR</option><option>KG</option><option>SET</option><option>BOX</option><option>ROLL</option>
              </select>
            </div>
            <div>
              <label className="block text-xs font-medium text-boss-text-muted mb-1">Description</label>
              <input value={form.description} onChange={e => setForm(f => ({...f, description: e.target.value}))} className="w-full px-3 py-2 rounded-lg border border-boss-border text-sm focus:ring-2 focus:ring-boss-violet/30 focus:outline-none" />
            </div>
            <div className="flex items-end">
              <button type="submit" className="px-6 py-2 bg-boss-violet text-white rounded-lg text-sm font-medium hover:bg-boss-violet-2" data-testid="product-submit-btn">{editItem ? 'Update' : 'Create'}</button>
            </div>
          </form>
        </div>
      )}

      <div className="bg-white rounded-xl shadow-sm border border-boss-border overflow-hidden">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b bg-boss-surface-2">
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Product</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">SKU</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Category</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">HSN</th>
              <th className="text-left py-3 px-4 font-medium text-boss-text-muted">Unit</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Status</th>
              <th className="text-center py-3 px-4 font-medium text-boss-text-muted">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? [...Array(3)].map((_, i) => <tr key={i}><td colSpan={7} className="py-3 px-4"><div className="h-4 bg-boss-surface-3 rounded animate-pulse" /></td></tr>) : products.length === 0 ? (
              <tr><td colSpan={7} className="py-12 text-center text-boss-text-muted">
                <Package size={24} className="mx-auto mb-2 opacity-50" />No products found. Add your first product.
              </td></tr>
            ) : products.map(p => (
              <tr key={p._id} className="border-b border-boss-border/50 hover:bg-boss-surface-2/50">
                <td className="py-2.5 px-4 font-medium">{p.product_name}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{p.sku || '---'}</td>
                <td className="py-2.5 px-4">{p.category || '---'}</td>
                <td className="py-2.5 px-4 font-mono text-xs">{p.hsn_code || '---'}</td>
                <td className="py-2.5 px-4">{p.unit || 'PCS'}</td>
                <td className="py-2.5 px-4 text-center">
                  <span className={`inline-block px-2 py-0.5 rounded-full text-xs ${p.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
                    {p.is_active !== false ? 'Active' : 'Inactive'}
                  </span>
                </td>
                <td className="py-2.5 px-4 text-center">
                  <div className="flex items-center justify-center gap-2">
                    <button onClick={() => startEdit(p)} className="p-1 text-boss-violet hover:bg-boss-violet/10 rounded"><Edit2 size={14} /></button>
                    <button onClick={() => handleDelete(p._id)} className="p-1 text-red-500 hover:bg-red-50 rounded"><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
